/**
 * Notification routing helper — Refs #614
 *
 * Maps a notification (type + data) to a deep link within the SPA.
 * Centralizes all routing logic so backend notifications only need to
 * carry `type` + `data` (JSONB) — the frontend decides where to land.
 */

interface NotificationLike {
  type: string;
  data?: Record<string, unknown> | null;
  link?: string | null;
}

/**
 * Returns a react-router path (with query string) for the given notification.
 * Falls back to `notification.link` if present, then to '/' (dashboard).
 */
export function getNotificationRoute(notification: NotificationLike): string {
  const { type, data } = notification;
  const d = (data || {}) as Record<string, string | undefined>;

  switch (type) {
    // Team/collaboration notifications → scroll to team widget on dashboard
    case 'invitation_received':
    case 'invitation_accepted':
    case 'member_joined':
    case 'member_left':
      return '/?widget=team';

    // Removed member: no deep-link target, just dashboard
    case 'member_removed':
      return '/';

    // Project deadline reminders: deep-link to dashboard project-instellingen
    case 'project_due_soon':
    case 'project_overdue':
      return '/?widget=settings';

    // Comment/mention: deep-link into the entity that holds the comment
    case 'comment_reply':
    case 'mention': {
      const entityType = d.entityType;
      const entityId = d.entityId;
      if (!entityId) return '/';
      switch (entityType) {
        case 'person':
          return `/people?personId=${encodeURIComponent(entityId)}`;
        case 'source':
          return `/sources?highlight=${encodeURIComponent(entityId)}`;
        case 'story':
          return `/stories?edit=${encodeURIComponent(entityId)}`;
        case 'media':
          return '/media';
        case 'place':
          return '/places';
        default:
          return '/';
      }
    }

    // Activity feed
    case 'project_activity':
      return '/audit';

    // System / fallback
    case 'system':
    default:
      // Honour backend-provided link as last resort, sanitise obvious broken paths
      if (notification.link && notification.link !== '/settings') {
        return notification.link;
      }
      return '/';
  }
}
